import { CircularProgress } from "@mui/material";
import { Button } from "components/Button/Button";
import { IconCollection } from "components/Icon/IconCollection";
import { FeConfigType } from "config/config";
import ActionToolbar, {
    ActionToolbarProps,
} from "containers/Resources/components/CommonActions/ActionToolbar";
import { ListActionProgress } from "containers/Resources/components/CommonActions/ListActionProgress";
import { Record } from "graphql/generated/graphql";
import { isEqual } from "lodash";
import { MUIDataTableOptions, SelectableRows } from "mui-datatables";
import { createContext, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./RecordTable.scss";
import StickyHorizontalScrollbar from "./StickyHorizontalScrollbar";
import Table from "./Table";
import TableColumnsSelector from "./TableColumnsSelector";
import TableDensitySelector from "./TableDensitySelector";
import {
    TableColumn,
    TableDensity,
    convertLegacyColumnToMuiColumn,
    getInitialColumnWidths,
    getInitialDensity,
    getInitialTableColumns,
    getSortOrder,
    getTableSettings,
    readSessionSegmentPage,
    updateLocalStorageTableSettings,
    writeSessionSegmentPage,
} from "./tableUtils";

interface RecordTableProps<T> {
    columns: TableColumn[];
    data: T[];
    totalItemCount: number;
    isLoading: boolean;
    recordTypes: FeConfigType | "all" | null;
    updateQuery?: (change: any) => void;
    refresh?: () => void;
    options?: {
        selectableRows?: SelectableRows;
        /**
         * Toolbar mode:
         * - `undefined` / `"custom"` -> the full RecordTable toolbar (left
         *   `ActionToolbar` with bulk row actions + right refresh/density/columns
         *   controls).
         * - `"controls"` -> same right-side controls (refresh/density/columns)
         *   but WITHOUT the left `ActionToolbar` — for compact related-record
         *   panels (e.g. UsedByList, RecordLog) that use `selectableRows: "none"`
         *   and want column-selection without the stray bulk-action chrome.
         * - `"default"` -> mui-datatables' own default toolbar.
         * - `"none"` -> no toolbar at all.
         */
        toolbar?: "default" | "custom" | "controls" | "none";
        toolbarFilters?: boolean;
        customRightToolbarChildren?: React.ReactNode;
        /**
         * Extra nodes rendered on the LEFT of the toolbar, before the action
         * buttons (Move, …). Use for a container-level create/upload action that
         * belongs with the row-action buttons rather than the table-display
         * controls on the right. Unlike `customLeftToolbar` (which REPLACES the
         * default ActionToolbar), this only prepends to it.
         */
        customLeftToolbarChildren?: Node | Node[];
        customLeftToolbar?: (props: ActionToolbarProps<T>) => Node | Node[];
        density?: TableDensity;
        /**
         * Show the per-table row-density toggle in the toolbar (default true).
         * The chosen density persists per table under `tableSettingsKey`,
         * overriding the caller-provided `density` default. Set false to hide
         * the control (e.g. tables that must keep a fixed density).
         */
        showDensityToggle?: boolean;
        /**
         * Allow the user to drag column borders to resize columns; the chosen
         * pixel widths persist per table under `tableSettingsKey` and override
         * the type-aware default width caps. Double-clicking a grip clears the
         * override. Defaults to true. Set false for tables that must keep fixed
         * column sizing.
         */
        resizableColumns?: boolean;
        setRowProps?: (row: any[], dataIndex: number, rowIndex: number) => object;
        jumpToPage?: boolean;
        showSearch?: boolean;
        currentOffset?: number;
        isSearchAlwaysOpen?: boolean;
        viewColumns?: boolean;
        customFooter?: () => React.ReactNode;
        canSearchEverywhere?: boolean;
        skipFetchOnAction?: boolean;
        /**
         * Show a manual "Refresh" button in the toolbar for non-live views (e.g.
         * advanced search, resource lists) that fetch once and would otherwise go
         * stale until navigating away. No-op unless a `refresh` callback is also
         * passed. Live views (dashboard widgets with their own live-bar) should
         * NOT set this — they already have a refresh affordance.
         */
        showRefreshButton?: boolean;
        rowsPerPageOptions?: number[];
        tableBodyMaxHeight?: string;
        /**
         * Hide the pagination footer and render all rows (mui-datatables
         * `pagination`). Used by height-adaptive callers (e.g. the dashboard
         * record_list widget) that size the fetched row count to the available
         * height and therefore want a single, footer-less page. Defaults to true.
         */
        pagination?: boolean;
        isRowSelectable?: (dataIndex: number) => boolean;
        /**
         * Show a horizontal scrollbar pinned to the bottom of the scroll
         * viewport ("sticky-to-viewport-bottom") for wide tables that overflow
         * horizontally, so the scrollbar is discoverable and reachable without
         * scrolling to the very bottom of a long list. Only has an effect when
         * the table's own horizontal scroll container (mui-datatables
         * `responsiveBase`) actually scrolls — i.e. contexts that keep
         * `overflow-x: auto` on it (e.g. the object_template CO / subrecord
         * lists). Defaults to false. See operator-task-fe-wide-list-top-or-
         * sticky-horizontal-scrollbar.
         */
        stickyHorizontalScrollbar?: boolean;
        /** Remember current page for client-side tables (no updateQuery) in sessionStorage. */
        clientSidePaginationPersist?: {
            storageKey: string;
            segmentKey?: string;
        };
    };
    passSelectedItemsToParent?: (selectedItems: any[]) => void;
    shouldResetSelection?: boolean;
    initialQuery?: { limit: number; offset?: number; order: string };
    tableSettingsKey?: string;
    onSearchChange?: (searchText: string | null) => void;
    searchPlaceholder?: string;
    className?: string;
    isActionButtonDisabled?: boolean;
    actionButtonLabel?: string;
    filterParams?: {
        isTopLevel?: boolean;
        deletedOnly?: boolean;
        normalOnly?: boolean;
    };
    isContainerGitEnabled?: boolean;
    isBundle?: boolean;
    isDisabled?: boolean;
}

/**
 * Data consumed by {@link RecordTableToolbar}. Provided via context so the
 * toolbar keeps a stable component identity — mui-datatables remounts
 * `components.TableToolbar` whenever the component *reference* changes, which
 * would snap shut an open columns popover and reset toolbar-local state on
 * every spinner toggle or row (de)selection.
 */
interface RecordTableToolbarContextValue {
    formattedSelectedItems: { resourceName: any; resourceData: any }[];
    customLeftToolbar?: (props: ActionToolbarProps<any>) => Node | Node[];
    recordTypes: FeConfigType | "all" | null;
    afterButtonAction: () => void;
    isActionButtonDisabled?: boolean;
    actionButtonLabel?: string;
    filterParams?: RecordTableProps<any>["filterParams"];
    isContainerGitEnabled?: boolean;
    isBundle?: boolean;
    isLoading: boolean;
    customRightToolbarChildren?: React.ReactNode;
    customLeftToolbarChildren?: Node | Node[];
    viewColumns?: boolean;
    /**
     * Hide the left-hand list/bulk-action chrome (`ActionToolbar` /
     * `customLeftToolbar` / `customLeftToolbarChildren`), keeping only the
     * right-side display controls. Set by the `"controls"` toolbar mode.
     */
    hideListActions?: boolean;
    /** Whether to render the manual refresh button (already AND-ed with `refresh` presence). */
    showRefreshButton: boolean;
    setCols: React.Dispatch<React.SetStateAction<TableColumn[]>>;
    tableSettingsKey: string;
    columns: TableColumn[];
    density: TableDensity;
    setDensity: (density: TableDensity) => void;
    showDensityToggle: boolean;
}

const RecordTableToolbarContext = createContext<RecordTableToolbarContextValue | null>(null);

/**
 * Stable toolbar component. Its identity never changes, so mui-datatables never
 * remounts it; dynamic data flows in through {@link RecordTableToolbarContext},
 * so context changes re-render (rather than remount) the toolbar.
 */
function RecordTableToolbar() {
    const ctx = useContext(RecordTableToolbarContext);
    if (!ctx) return null;
    const {
        formattedSelectedItems,
        customLeftToolbar,
        recordTypes,
        afterButtonAction,
        isActionButtonDisabled,
        actionButtonLabel,
        filterParams,
        isContainerGitEnabled,
        isBundle,
        isLoading,
        customRightToolbarChildren,
        customLeftToolbarChildren,
        viewColumns,
        hideListActions,
        showRefreshButton,
        setCols,
        tableSettingsKey,
        columns,
        density,
        setDensity,
        showDensityToggle,
    } = ctx;

    return (
        <div className="toolbar">
            <div className="left">
                {!hideListActions && (
                    <>
                        <ListActionProgress />
                        {customLeftToolbarChildren && (
                            <span className="customLeftToolbarChildren">
                                {customLeftToolbarChildren}
                            </span>
                        )}
                        {customLeftToolbar ? (
                            customLeftToolbar({
                                selectedItems: formattedSelectedItems,
                                recordTypes,
                                afterButtonAction,
                                isDisabled: isActionButtonDisabled,
                                actionButtonLabel,
                                filterParams:
                                    tableSettingsKey === "execution_filter"
                                        ? filterParams
                                        : undefined,
                            })
                        ) : (
                            <ActionToolbar
                                selectedItems={formattedSelectedItems}
                                recordTypes={recordTypes}
                                afterButtonAction={afterButtonAction}
                                isContainerGitEnabled={isContainerGitEnabled}
                                isBundle={isBundle}
                            />
                        )}
                    </>
                )}
            </div>
            <div className="spacer">
                <CircularProgress
                    disableShrink
                    className={`spinner ${isLoading ? "" : "hidden"}`}
                />
            </div>
            <div className="right">
                {customRightToolbarChildren}
                {showRefreshButton && (
                    <Button
                        variation="clear"
                        label="Refresh"
                        title="Refresh"
                        icon={IconCollection.refresh}
                        loading={isLoading}
                        disabled={isLoading}
                        onClick={afterButtonAction}
                    />
                )}
                {showDensityToggle && (
                    <TableDensitySelector density={density} setDensity={setDensity} />
                )}
                {viewColumns !== false && (
                    <TableColumnsSelector
                        setTableColumns={setCols}
                        tableSettingsKey={tableSettingsKey}
                        initialColumns={columns}
                    />
                )}
            </div>
        </div>
    );
}

/**
 * A comprehensive data table component built on top of MUI DataTable
 * with support for server-side operations, row selection, custom toolbars,
 * and persistent table settings.
 *
 * @template T - The type of data records, must extend Record with id and optional name
 * @param props - RecordTableProps object containing all configuration
 * @returns JSX.Element - The rendered table component
 *
 */
function RecordTable<T extends Pick<Record, "id"> & { name?: string }>({
    columns,
    data,
    totalItemCount,
    isLoading,
    shouldResetSelection,
    recordTypes,
    updateQuery,
    refresh,
    options,
    tableSettingsKey = "",
    initialQuery,
    onSearchChange,
    searchPlaceholder,
    className,
    isActionButtonDisabled,
    actionButtonLabel,
    passSelectedItemsToParent,
    isContainerGitEnabled,
    isBundle,
    isDisabled,
    filterParams,
}: RecordTableProps<T>) {
    const initialSelection: Array<any> = [];
    const [selectedItems, setSelectedItems] = useState(initialSelection);
    const [selectedIndexes, setSelectedIndexes] = useState(initialSelection);
    const [rowsPerPage, setRowsPerPage] = useState<number>(initialQuery?.limit || 10);
    const [cols, setCols] = useState<TableColumn[]>([]);

    useEffect(
        () => setCols(getInitialTableColumns(columns, tableSettingsKey)),
        [columns, tableSettingsKey]
    );

    // Wrapper element for the optional sticky-to-viewport-bottom horizontal
    // scrollbar to resolve the mui-datatables responsiveBase scroll node beneath.
    const recordTableWrapperRef = useRef<HTMLDivElement>(null);

    // Per-table row density: the persisted user choice (settings key) overrides
    // the caller-provided `options.density` default; falls back to "regular".
    const densityDefault = options?.density ?? "regular";
    const [density, setDensity] = useState<TableDensity>(() =>
        getInitialDensity(tableSettingsKey, densityDefault)
    );
    useEffect(() => {
        setDensity(getInitialDensity(tableSettingsKey, densityDefault));
    }, [tableSettingsKey, densityDefault]);
    const applyDensity = useCallback(
        (newDensity: TableDensity) => {
            setDensity(newDensity);
            updateLocalStorageTableSettings(tableSettingsKey, { density: newDensity });
        },
        [tableSettingsKey]
    );

    // Per-column drag-to-resize widths (px), keyed by column key. Persisted per
    // table under the settings key, overriding the type-aware default caps.
    // During a drag `applyColumnWidth` is called with commit=false on every
    // mouse-move (state-only, live preview) and commit=true once on release
    // (state + persist), so we don't thrash localStorage mid-drag.
    const resizableColumns = options?.resizableColumns ?? true;
    // NB: the global `Record` utility is shadowed in this file by the imported
    // Cloudomation `Record` GraphQL type, so use an index signature here.
    const [columnWidths, setColumnWidths] = useState<{ [key: string]: number }>(() =>
        getInitialColumnWidths(tableSettingsKey)
    );
    // Mirror the latest widths in a ref so the stable callbacks below can build
    // the next map without capturing a stale `columnWidths` (they intentionally
    // depend only on `tableSettingsKey`) and without side-effecting inside a
    // setState updater.
    const columnWidthsRef = useRef(columnWidths);
    columnWidthsRef.current = columnWidths;
    useEffect(() => {
        setColumnWidths(getInitialColumnWidths(tableSettingsKey));
    }, [tableSettingsKey]);
    const applyColumnWidth = useCallback(
        (columnKey: string, width: number, commit: boolean) => {
            const next = { ...columnWidthsRef.current, [columnKey]: width };
            columnWidthsRef.current = next;
            setColumnWidths(next);
            // Persist only on release (commit) so a drag doesn't thrash storage.
            if (commit) {
                updateLocalStorageTableSettings(tableSettingsKey, { widths: next });
            }
        },
        [tableSettingsKey]
    );
    const resetColumnWidth = useCallback(
        (columnKey: string) => {
            if (!(columnKey in columnWidthsRef.current)) return;
            const next = { ...columnWidthsRef.current };
            delete next[columnKey];
            columnWidthsRef.current = next;
            setColumnWidths(next);
            updateLocalStorageTableSettings(tableSettingsKey, { widths: next });
        },
        [tableSettingsKey]
    );

    // Prefer a persisted sort (column + direction) stored under this table's
    // settings key over the caller's default `initialQuery.order`, so a user's
    // chosen sort survives a reload. The write side already exists — any table
    // with a `tableSettingsKey` writes its sort to the `order` setting in
    // onColumnSortChange (including client-side tables like the dashboard
    // record_list widget, which otherwise pass a hardcoded default order) — this
    // is the matching restore. For server-side callers the persisted order is
    // already folded into `initialQuery.order` (getInitialQueryWithTableSettings),
    // so preferring it here is idempotent. A persisted order whose column is gone
    // (or no longer sortable) in the current column set is ignored, falling back
    // to `initialQuery.order`.
    const persistedOrder = useMemo(() => {
        if (!tableSettingsKey) return null;
        const stored = getTableSettings(tableSettingsKey, "order");
        if (typeof stored !== "string" || stored.length < 2) return null;
        const sign = stored.slice(0, 1);
        if (sign !== "+" && sign !== "-") return null;
        const col = columns.find((c) => c.key === stored.slice(1));
        if (!col || col.sortable === false) return null;
        return stored;
    }, [tableSettingsKey, columns]);

    const effectiveInitialOrder = persistedOrder ?? initialQuery?.order;

    const initialSortOrder = useMemo(
        () =>
            effectiveInitialOrder
                ? {
                      name: getSortOrder(effectiveInitialOrder).name,
                      direction: getSortOrder(effectiveInitialOrder).direction,
                  }
                : null,
        [effectiveInitialOrder]
    );
    const [sortOrder, setSortOrder] = useState(initialSortOrder);

    useEffect(() => {
        setSortOrder(initialSortOrder);
    }, [initialSortOrder, recordTypes]);

    useEffect(() => {
        setRowsPerPage(initialQuery?.limit || 10);
    }, [initialQuery?.limit, recordTypes]);

    const clientPageCfg = !updateQuery ? options?.clientSidePaginationPersist : undefined;
    const clientPagePersist = Boolean(clientPageCfg?.storageKey);
    const clientPageStorageKey = clientPageCfg?.storageKey ?? "";
    const clientPageSegmentKey = clientPageCfg?.segmentKey ?? "_";

    const [clientTablePage, setClientTablePage] = useState(() => {
        if (!clientPageCfg?.storageKey) return 0;
        return readSessionSegmentPage(
            clientPageCfg.storageKey,
            clientPageCfg.segmentKey ?? "_"
        );
    });

    useEffect(() => {
        if (!clientPagePersist) {
            setClientTablePage(0);
            return;
        }
        setClientTablePage(
            readSessionSegmentPage(clientPageStorageKey, clientPageSegmentKey)
        );
    }, [clientPagePersist, clientPageStorageKey, clientPageSegmentKey]);

    useEffect(() => {
        if (!clientPagePersist) return;
        const maxPageIndex = Math.max(0, Math.ceil((totalItemCount || 0) / rowsPerPage) - 1);
        setClientTablePage((prev) => {
            if (prev <= maxPageIndex) return prev;
            writeSessionSegmentPage(clientPageStorageKey, clientPageSegmentKey, maxPageIndex);
            return maxPageIndex;
        });
    }, [
        clientPagePersist,
        clientPageStorageKey,
        clientPageSegmentKey,
        totalItemCount,
        rowsPerPage,
    ]);

    const maxPageIndexForClient = Math.max(
        0,
        Math.ceil((totalItemCount || 0) / rowsPerPage) - 1
    );
    const clientTablePageForMui = clientPagePersist
        ? Math.min(clientTablePage, maxPageIndexForClient)
        : undefined;

    // Sync selected items with current data to handle data updates
    useEffect(() => {
        // eslint-disable-next-line eqeqeq
        if (selectedItems.length && data != undefined) {
            const selectedData: { selectedIndexes: number[]; selectedItems: T[] } = {
                selectedIndexes: [],
                selectedItems: [],
            };

            // Find current indices of selected items in the data array
            selectedItems.forEach((selectedItem) => {
                const selectedIndex = data.findIndex((item) => {
                    return item.id ? item.id === selectedItem.id : item.name === selectedItem.name;
                });

                if (selectedIndex > -1) {
                    selectedData.selectedIndexes.push(selectedIndex);
                    selectedData.selectedItems.push(selectedItem);
                }
            });

            setSelectedIndexes(selectedData.selectedIndexes);
            // Only update selected items if they've actually changed
            if (!isEqual(selectedItems, selectedData.selectedItems)) {
                setSelectedItems(selectedData.selectedItems);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const resetSelectionAndRefresh = useCallback(() => {
        setSelectedIndexes([]);
        setSelectedItems([]);
        if (refresh && !options?.skipFetchOnAction) refresh();
    }, [options?.skipFetchOnAction, refresh]);

    useEffect(() => {
        if (shouldResetSelection) {
            resetSelectionAndRefresh();
        }
    }, [shouldResetSelection, resetSelectionAndRefresh]);

    // Notify parent component of selected items changes
    useEffect(
        () => passSelectedItemsToParent?.(selectedItems),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedItems, passSelectedItemsToParent]
    );

    const toolbarContextValue = useMemo<RecordTableToolbarContextValue>(
        () => ({
            formattedSelectedItems: selectedItems.map((x) => ({
                resourceName: x.resource_type || x.activity_type,
                resourceData: x,
            })),
            customLeftToolbar: options?.customLeftToolbar,
            recordTypes,
            afterButtonAction: resetSelectionAndRefresh,
            isActionButtonDisabled,
            actionButtonLabel,
            filterParams,
            isContainerGitEnabled,
            isBundle,
            isLoading,
            customRightToolbarChildren: options?.customRightToolbarChildren,
            customLeftToolbarChildren: options?.customLeftToolbarChildren,
            viewColumns: options?.viewColumns,
            hideListActions: options?.toolbar === "controls",
            showRefreshButton: Boolean(options?.showRefreshButton && refresh),
            setCols,
            tableSettingsKey,
            columns,
            density,
            setDensity: applyDensity,
            showDensityToggle: options?.showDensityToggle ?? true,
        }),
        [
            selectedItems,
            density,
            applyDensity,
            options?.showDensityToggle,
            options?.customLeftToolbar,
            options?.customRightToolbarChildren,
            options?.customLeftToolbarChildren,
            options?.viewColumns,
            options?.toolbar,
            options?.showRefreshButton,
            refresh,
            recordTypes,
            resetSelectionAndRefresh,
            isActionButtonDisabled,
            actionButtonLabel,
            filterParams,
            isContainerGitEnabled,
            isBundle,
            isLoading,
            tableSettingsKey,
            columns,
        ]
    );

    // Stable component identity — mui-datatables remounts the toolbar subtree
    // whenever this reference changes, so it must not be recreated per render.
    const effectiveToolbar =
        !options?.toolbar ||
        options?.toolbar === "custom" ||
        options?.toolbar === "controls"
            ? RecordTableToolbar
            : options.toolbar === "default"
            ? undefined
            : null;

    // Stable event handlers. These MUST NOT be recreated per render: they feed
    // `tableOptions` below, and mui-datatables' `componentDidUpdate` triggers a
    // full O(rows×columns) table rebuild whenever the `options` prop *reference*
    // changes. Keeping the handlers (and therefore `tableOptions`) referentially
    // stable across re-renders that don't change their inputs is what prevents a
    // live-subscription re-render (or any parent re-render) from rebuilding all
    // rows — the page-size-100 "fans keep whirring" cost.
    // (operator-task-fe-advanced-search-co-list-costly-search-perf)
    const handleChangePage = useCallback(
        (currentPage: number) => {
            if (updateQuery) updateQuery({ offset: currentPage * rowsPerPage });
            if (clientPagePersist) {
                setClientTablePage(currentPage);
                writeSessionSegmentPage(clientPageStorageKey, clientPageSegmentKey, currentPage);
            }
            setSelectedIndexes([]);
            setSelectedItems([]);
        },
        [updateQuery, rowsPerPage, clientPagePersist, clientPageStorageKey, clientPageSegmentKey]
    );

    const handleChangeRowsPerPage = useCallback(
        (numberOfRows: number) => {
            setRowsPerPage(numberOfRows);
            if (updateQuery) updateQuery({ limit: numberOfRows });
            updateLocalStorageTableSettings(tableSettingsKey, { limit: numberOfRows });
            if (clientPagePersist) {
                setClientTablePage(0);
                writeSessionSegmentPage(clientPageStorageKey, clientPageSegmentKey, 0);
            }
        },
        [updateQuery, tableSettingsKey, clientPagePersist, clientPageStorageKey, clientPageSegmentKey]
    );

    const handleColumnSortChange = useCallback(
        (changedColumn: string, direction: string) => {
            if (!updateQuery && !tableSettingsKey) return;

            const initialOrder = direction === "none" && initialQuery?.order;

            const newSortOrder =
                changedColumn === initialQuery?.order.slice(1) && initialOrder && "asc";
            const newQueryOrder = newSortOrder && `+${changedColumn}`;

            setSortOrder({
                name: initialOrder ? getSortOrder(initialOrder).name : changedColumn,
                direction: initialOrder
                    ? newSortOrder || getSortOrder(initialOrder).direction
                    : (direction as "asc" | "desc"),
            });
            const newOrder = initialOrder
                ? newQueryOrder || initialOrder
                : `${direction === "asc" ? "+" : "-"}${changedColumn}`;

            if (updateQuery) {
                updateQuery({
                    order: newOrder,
                });
            }
            if (tableSettingsKey) {
                updateLocalStorageTableSettings(tableSettingsKey, { order: newOrder });
            }
        },
        [updateQuery, tableSettingsKey, initialQuery]
    );

    const handleRowSelectionChange = useCallback(
        (currentRowsSelected: any[], allRowsSelected: any[], rowsSelected?: any[]) => {
            const selectedIndexes = allRowsSelected.map((x) => x.dataIndex);
            setSelectedIndexes(selectedIndexes);
            const selectedIndexesSet = new Set(selectedIndexes);
            setSelectedItems(
                data.filter((elem: any, index: number) => selectedIndexesSet.has(index))
            );
        },
        [data]
    );

    const tableOptions: MUIDataTableOptions = useMemo(
        () => ({
            selectableRows: options?.selectableRows ?? "multiple",
            rowsSelected: selectedIndexes,
            selectToolbarPlacement: "none",
            serverSide: updateQuery ? true : false,
            count: totalItemCount,
            rowsPerPage: rowsPerPage,
            rowsPerPageOptions: options?.rowsPerPageOptions ?? [5, 10, 30, 100],
            setRowProps: options?.setRowProps,
            onChangePage: handleChangePage,
            onChangeRowsPerPage: handleChangeRowsPerPage,
            onColumnSortChange: handleColumnSortChange,
            onRowSelectionChange: handleRowSelectionChange,
            filter: options?.toolbarFilters ?? true,
            sortOrder: sortOrder || undefined,
            onSearchChange: onSearchChange,
            searchPlaceholder: searchPlaceholder,
            jumpToPage: options?.jumpToPage ?? true,
            search: options?.showSearch ?? true,
            searchAlwaysOpen: options?.isSearchAlwaysOpen ?? false,
            page: clientPagePersist
                ? clientTablePageForMui
                : options?.currentOffset === 0
                ? 0
                : undefined,
            viewColumns: options?.viewColumns ?? true,
            customFooter: options?.customFooter,
            tableBodyMaxHeight: options?.tableBodyMaxHeight, // Enable scrolling for table body only
            pagination: options?.pagination ?? true,
            isRowSelectable: options?.isRowSelectable,
        }),
        [
            options?.selectableRows,
            selectedIndexes,
            updateQuery,
            totalItemCount,
            rowsPerPage,
            options?.rowsPerPageOptions,
            options?.setRowProps,
            handleChangePage,
            handleChangeRowsPerPage,
            handleColumnSortChange,
            handleRowSelectionChange,
            options?.toolbarFilters,
            sortOrder,
            onSearchChange,
            searchPlaceholder,
            options?.jumpToPage,
            options?.showSearch,
            options?.isSearchAlwaysOpen,
            clientPagePersist,
            clientTablePageForMui,
            options?.currentOffset,
            options?.viewColumns,
            options?.customFooter,
            options?.tableBodyMaxHeight,
            options?.pagination,
            options?.isRowSelectable,
        ]
    );

    // Stable fallback so a missing `refresh` doesn't produce a new function
    // identity — and therefore a full column rebuild — on every render.
    const noopRefresh = useMemo(() => () => {}, []);
    const muiColumns = useMemo(
        () =>
            cols.map((col: TableColumn) =>
                convertLegacyColumnToMuiColumn(col, data, refresh || noopRefresh, {
                    width: columnWidths[col.key],
                    resizable: resizableColumns,
                    onResize: applyColumnWidth,
                    onResetWidth: resetColumnWidth,
                })
            ),
        [cols, data, refresh, noopRefresh, columnWidths, resizableColumns, applyColumnWidth, resetColumnWidth]
    );

    return (
        <div
            ref={recordTableWrapperRef}
            className={`recordTable density-${density} ${className ?? ""}`}
        >
            <RecordTableToolbarContext.Provider value={toolbarContextValue}>
                <Table
                    columns={muiColumns}
                    data={data}
                    options={tableOptions}
                    isLoading={isLoading}
                    components={{
                        TableToolbar: effectiveToolbar,
                    }}
                    canSearchEverywhere={options?.canSearchEverywhere}
                    isDisabled={isDisabled}
                />
            </RecordTableToolbarContext.Provider>
            {options?.stickyHorizontalScrollbar && (
                <StickyHorizontalScrollbar tableWrapperRef={recordTableWrapperRef} />
            )}
        </div>
    );
}

/**
 * RecordTable backs every resource/search/dashboard list. It reads nothing from
 * Redux directly — all dynamic data arrives via props — so a live-subscription
 * push re-renders it only because a connected ancestor re-renders. With the
 * options/handlers/columns already memoized (so mui-datatables no longer rebuilds
 * the table), the residual cost is mui-datatables re-rendering ALL cells of the
 * mounted page on each such parent re-render (react-timeago id churn + cell
 * re-stamping) — the page-size-100 "fans keep whirring after load" cost.
 *
 * Wrapping in React.memo (default shallow prop comparison) short-circuits that:
 * when every prop reference is unchanged (fetch-once views like advanced search
 * during a live push), the whole subtree is skipped. It is safe by construction
 * for live-fed callers too — a genuine data change always arrives as a new `data`
 * reference, so the memo re-renders and never freezes live updates; an unstable-
 * prop caller merely gets a harmless no-op. The cast preserves the generic call
 * signature that React.memo would otherwise widen.
 * (operator-task-fe-advanced-search-co-list-costly-search-perf)
 */
export default memo(RecordTable) as typeof RecordTable;
